if (!customElements.get('product-modal')) {
	customElements.define(
		'product-modal',
		class ProductModal extends ModalDialog {
			constructor() {
				super();
				this.boundHandlePrevClick = this.handlePrevClick.bind(this);
				this.boundHandleNextClick = this.handleNextClick.bind(this);
				this.boundHandleKeydown = this.handleKeydown.bind(this);
				this.boundHandleZoomClick = this.handleZoomClick.bind(this);
				this.zoomedImage = null;
				this.lastTap = 0;
			}

			connectedCallback() {
				super.connectedCallback?.();
				this.setupNavigation();
				this.setupZoomIcons();
				this.setupVariantListener();
			}

			setupVariantListener() {
				if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
					subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
						this.updateMediaForVariant(event.data.variant);
					});
				}
			}

			updateMediaForVariant(variant) {
				if (!variant) return;

				const modalMediaItems = Array.from(
					this.querySelectorAll('.product-media-modal__media-item'),
				);
				const currentVariantTitle = variant.title.replace(/\s/g, '');
				const matchesCurrentVariant = (item) =>
					currentVariantTitle.includes(item.dataset.alt) || item.dataset.alt === 'no-variant';

				// Fallback : si aucune image ne correspond au variant, toutes les afficher
				const hasMatchingMedia = modalMediaItems.some(matchesCurrentVariant);

				let isActiveAdded = false;

				modalMediaItems.forEach((item) => {
					item.classList.remove('active');
					item.classList.remove('disabled-modal-slide');

					if (!hasMatchingMedia || matchesCurrentVariant(item)) {
						// Image correspond au variant sélectionné - afficher
						item.style.display = '';
						if (!isActiveAdded) {
							item.classList.add('active');
							isActiveAdded = true;
						}
					} else {
						// Image ne correspond pas au variant - masquer
						item.style.display = 'none';
						item.classList.add('disabled-modal-slide');
					}
				});
			}

			hide() {
				this.resetZoom();
				super.hide();
			}

			show(opener) {
				super.show(opener);
				this.showActiveMedia();
				setTimeout(() => {
					this.setupNavigation();
					this.setupZoomIcons();
				}, 100);
			}

			setupNavigation() {
				const prevButton = this.querySelector('.product-media-modal__nav-button--prev');
				const nextButton = this.querySelector('.product-media-modal__nav-button--next');

				if (prevButton && nextButton) {
					prevButton.removeEventListener('click', this.boundHandlePrevClick);
					nextButton.removeEventListener('click', this.boundHandleNextClick);
					prevButton.addEventListener('click', this.boundHandlePrevClick);
					nextButton.addEventListener('click', this.boundHandleNextClick);
				}

				if (!this.keyboardSetup) {
					this.keyboardSetup = true;
					this.addEventListener('keydown', this.boundHandleKeydown);
				}
			}

			handlePrevClick(e) {
				e.preventDefault();
				e.stopPropagation();
				e.stopImmediatePropagation();
				this.navigateMedia(-1);
				return false;
			}

			handleNextClick(e) {
				e.preventDefault();
				e.stopPropagation();
				e.stopImmediatePropagation();
				this.navigateMedia(1);
				return false;
			}

			handleKeydown(e) {
				if (!this.hasAttribute('open')) return;
				if (e.key === 'ArrowLeft') {
					e.preventDefault();
					this.navigateMedia(-1);
				} else if (e.key === 'ArrowRight') {
					e.preventDefault();
					this.navigateMedia(1);
				}
			}

			navigateMedia(direction) {
				this.resetZoom();

				// Ne prendre que les médias visibles (pas disabled-modal-slide)
				const allMedia = Array.from(
					this.querySelectorAll('[data-media-id]:not(.disabled-modal-slide)'),
				);
				const activeMedia = this.querySelector('[data-media-id].active');
				if (!activeMedia || allMedia.length === 0) return;

				let currentIndex = allMedia.indexOf(activeMedia);
				let nextIndex = currentIndex + direction;

				if (nextIndex >= allMedia.length) {
					nextIndex = 0;
				} else if (nextIndex < 0) {
					nextIndex = allMedia.length - 1;
				}

				const nextMedia = allMedia[nextIndex];
				if (nextMedia) {
					allMedia.forEach((media) => media.classList.remove('active'));
					nextMedia.classList.add('active');
					nextMedia.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

					const container = this.querySelector('[role="document"]');
					if (container) {
						container.scrollLeft =
							nextMedia.offsetLeft - container.clientWidth / 2 + nextMedia.width / 2;
					}
				}
			}

			setupZoomIcons() {
				const zoomIcons = this.querySelectorAll('.product__media-icon--lightbox');
				zoomIcons.forEach((icon) => {
					icon.removeEventListener('click', this.boundHandleZoomClick);
					icon.addEventListener('click', this.boundHandleZoomClick);
				});

				// Permettre aussi de cliquer sur les images pour zoomer
				const images = this.querySelectorAll('.product-media-modal__media-item img');
				images.forEach((img) => {
					// Intercepter click et pointerup (utilis� par ModalDialog)
					img.removeEventListener('click', this.boundHandleZoomClick);
					img.removeEventListener('pointerup', this.boundHandleZoomClick);
					img.addEventListener('click', this.boundHandleZoomClick);
					img.addEventListener('pointerup', this.boundHandleZoomClick);

					// Support double-tap sur mobile
					img.addEventListener('touchend', (e) => {
						const currentTime = new Date().getTime();
						const tapLength = currentTime - this.lastTap;

						if (tapLength < 300 && tapLength > 0) {
							e.preventDefault();
							e.stopPropagation();
							e.stopImmediatePropagation();
							this.boundHandleZoomClick(e);
						}

						this.lastTap = currentTime;
					});
				});
			}

			handleZoomClick(e) {
				e.preventDefault();
				e.stopPropagation();
				e.stopImmediatePropagation();

				const mediaItem = e.target.closest('.product-media-modal__media-item');
				if (!mediaItem) return;

				const img = mediaItem.querySelector('img');
				if (!img) return;

				if (this.zoomedImage === img) {
					this.resetZoom();
				} else {
					this.resetZoom();
					this.applyZoom(img, mediaItem);
				}
			}

			applyZoom(img, mediaItem) {
				this.zoomedImage = img;
				mediaItem.classList.add('zoomed');
				img.style.cursor = 'zoom-out';
				img.style.transition = 'transform 0.3s ease';
				img.style.transform = 'scale(2)';
				img.style.transformOrigin = 'center center';

				// Cacher la navigation et la croix de fermeture
				const navigation = this.querySelector('.product-media-modal__navigation');
				const closeButton = this.querySelector('.product-media-modal__toggle');
				if (navigation) navigation.style.setProperty('display', 'none', 'important');
				if (closeButton) closeButton.style.setProperty('display', 'none', 'important');

				const updateZoomPosition = (clientX, clientY) => {
					const rect = img.getBoundingClientRect();
					const x = ((clientX - rect.left) / rect.width) * 100;
					const y = ((clientY - rect.top) / rect.height) * 100;
					img.style.transformOrigin = `${x}% ${y}%`;
				};

				// Support souris (desktop)
				const handleMove = (e) => {
					updateZoomPosition(e.clientX, e.clientY);
				};

				// Support tactile (mobile)
				const handleTouchMove = (e) => {
					if (e.touches.length === 1) {
						e.preventDefault();
						const touch = e.touches[0];
						updateZoomPosition(touch.clientX, touch.clientY);
					}
				};

				// Bloquer la propagation de tous les �v�nements pointer sur le mediaItem zoom�
				const blockPointerPropagation = (e) => {
					e.stopPropagation();
					e.stopImmediatePropagation();
				};

				img.addEventListener('mousemove', handleMove);
				img.addEventListener('touchmove', handleTouchMove, { passive: false });
				mediaItem.addEventListener('pointerup', blockPointerPropagation, true);
				mediaItem.addEventListener('pointerdown', blockPointerPropagation, true);

				img._handleMove = handleMove;
				img._handleTouchMove = handleTouchMove;
				img._blockPointerPropagation = blockPointerPropagation;
				img._mediaItem = mediaItem;
			}

			resetZoom() {
				if (this.zoomedImage) {
					const mediaItem = this.zoomedImage.closest('.product-media-modal__media-item');
					if (mediaItem) {
						mediaItem.classList.remove('zoomed');
					}

					// Réafficher la navigation et la croix de fermeture
					const navigation = this.querySelector('.product-media-modal__navigation');
					const closeButton = this.querySelector('.product-media-modal__toggle');
					if (navigation) navigation.style.removeProperty('display');
					if (closeButton) closeButton.style.removeProperty('display');

					this.zoomedImage.style.cursor = '';
					this.zoomedImage.style.transform = '';
					this.zoomedImage.style.transformOrigin = '';

					if (this.zoomedImage._handleMove) {
						this.zoomedImage.removeEventListener('mousemove', this.zoomedImage._handleMove);
						delete this.zoomedImage._handleMove;
					}

					if (this.zoomedImage._handleTouchMove) {
						this.zoomedImage.removeEventListener('touchmove', this.zoomedImage._handleTouchMove);
						delete this.zoomedImage._handleTouchMove;
					}

					if (this.zoomedImage._blockPointerPropagation && this.zoomedImage._mediaItem) {
						this.zoomedImage._mediaItem.removeEventListener(
							'pointerup',
							this.zoomedImage._blockPointerPropagation,
							true,
						);
						this.zoomedImage._mediaItem.removeEventListener(
							'pointerdown',
							this.zoomedImage._blockPointerPropagation,
							true,
						);
						delete this.zoomedImage._blockPointerPropagation;
						delete this.zoomedImage._mediaItem;
					}

					this.zoomedImage = null;
				}
			}

			showActiveMedia() {
				this.querySelectorAll(
					`[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`,
				).forEach((element) => {
					element.classList.remove('active');
				});
				const activeMedia = this.querySelector(
					`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`,
				);
				const activeMediaTemplate = activeMedia.querySelector('template');
				const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
				activeMedia.classList.add('active');
				activeMedia.scrollIntoView();

				const container = this.querySelector('[role="document"]');
				container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

				if (
					activeMedia.nodeName == 'DEFERRED-MEDIA' &&
					activeMediaContent &&
					activeMediaContent.querySelector('.js-youtube')
				)
					activeMedia.loadContent();
			}
		},
	);
}
